import { resolveRoomIdByAccessCodeRTDB, getRoomSnapshotRTDB } from "./roomFunctions";

const EVENT_JOIN_EARLY_WINDOW_MS = 30 * 60 * 1000;

const buildPlayerPayload = (userData, isHost = false) => ({
  id: userData.id,
  username: userData.username,
  avatar: userData.avatar,
  orbAvatar: userData.orbAvatar || null,
  podium: userData.podium || null,
  email: userData.email,
  record: userData.record || { wins: 0, losses: 0 },
  level: userData.level || 1,
  partyId: userData.partyId || null,
  xp: userData.xp || 0,
  sticker: userData.sticker || null,
  selectedCardSlap: Number(userData?.selectedCardSlap || 1),
  selectedTradeAnimation: Number(
    userData?.selectedTradeAnimation || userData?.selectedTradAnimation || 1
  ),
  isHost,
});

export const getRoomByAccessCodeRTDB = async (accessCode) => {
  const resolved = await resolveRoomIdByAccessCodeRTDB(accessCode);
  const roomData = await getRoomSnapshotRTDB(resolved.roomId);
  if (!roomData) throw new Error("Room not found.");
  return { roomId: resolved.roomId, roomData };
};

export const joinRoomRTDBWithValidation = async ({
  userData,
  joinAndSetRoom,
  accessCode = null,
  roomNodeId = null,
}) => {
  if (!userData?.id) {
    throw new Error("You must be signed in to join a room.");
  }
  if (typeof joinAndSetRoom !== "function") {
    throw new Error("Room join handler is unavailable.");
  }

  const player = buildPlayerPayload(userData, false);

  let matchedRoomId = roomNodeId;
  let roomData = null;

  if (matchedRoomId) {
    roomData = await getRoomSnapshotRTDB(matchedRoomId);
    if (!roomData) throw new Error("Room not found.");

    if (
      accessCode != null &&
      String(roomData?.accessCode ?? "").trim() !== String(accessCode).trim()
    ) {
      throw new Error("Access code does not match this event.");
    }
  } else {
    const resolved = await getRoomByAccessCodeRTDB(accessCode);
    matchedRoomId = resolved.roomId;
    roomData = resolved.roomData;
  }

  const wagerAmount = Number(roomData?.wager?.amount ?? 0);
  const wagerCurrency = roomData?.wager?.currency || "tokens";
  const isPrivilegedEventJoiner =
    !!userData?.roles?.includes?.("admin") || !!userData?.roles?.includes?.("manager");
  const eventStartMs = Number(roomData?.eventMeta?.scheduledStartMs ?? 0);
  const eventAutoStart = !!roomData?.eventMeta?.autoStart;

  if (
    eventAutoStart &&
    eventStartMs > 0 &&
    !isPrivilegedEventJoiner &&
    eventStartMs - Date.now() > EVENT_JOIN_EARLY_WINDOW_MS
  ) {
    throw new Error("This event lobby opens 30 minutes before the scheduled start time.");
  }

  if (wagerAmount > 0) {
    if (wagerCurrency === "eth") {
      const battleWalletBalanceRaw = userData?.battleWalletBalance;
      if (
        battleWalletBalanceRaw === undefined ||
        battleWalletBalanceRaw === null
      ) {
        throw new Error(
          "This room requires an ETH wager. Please top up your Battle Wallet before joining.",
        );
      }

      const battleWalletBalance = Number(battleWalletBalanceRaw ?? 0);
      if (battleWalletBalance < wagerAmount) {
        throw new Error(
          `Not enough ETH in your Battle Wallet. You need ${wagerAmount} ETH. Please top up to join.`,
        );
      }
    } else {
      const userTokenBalance = Number(userData?.infinityTokens ?? 0);
      if (userTokenBalance < wagerAmount) {
        throw new Error(
          `You need at least ${wagerAmount} Infinity Tokens to join this room. Visit the Store to purchase more.`,
        );
      }
    }
  }

  const playersArray = Object.values(roomData?.players || {});
  const maxHumans = Math.floor((roomData?.amountOfPlayers || 0) / 2);
  const normalizedMode = String(roomData?.mode || "").toLowerCase();
  const isPveRoom = normalizedMode
    ? normalizedMode === "pve"
    : !!roomData?.pve;
  const isInRoom = playersArray.some((p) => p.id === userData.id);

  if (!isInRoom) {
    if (isPveRoom && playersArray.length >= maxHumans) {
      throw new Error("PvE room is full.");
    }

    if (playersArray.length >= roomData?.amountOfPlayers) {
      throw new Error("Room is full.");
    }

    await joinAndSetRoom(matchedRoomId, userData.id, {
      ...player,
      ready: false,
      isBot: false,
      character: null,
    });
  }

  return {
    roomId: matchedRoomId,
    roomData,
    joined: !isInRoom,
  };
};

